import axios from 'axios';

// API base URL: use env override if provided, otherwise use relative URLs (same origin)
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  csrf_token?: string;
  token_type: string;
  user_type: string;
  expires_in: number;
  doctor?: {
    id: number;
    email: string;
    is_verified: boolean;
  };
}

export interface GoogleSsoRequest {
  id_token: string;
}

class AuthService {
  private token: string | null = null;
  private csrfToken: string | null = null;

  // Email/Password login
  async loginWithEmailPassword(email: string, password: string): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(
      apiUrl('/api/v1/auth/login?role=doctor'),
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    this.token = response.data.access_token;
    this.csrfToken = response.data.csrf_token || null;

    // Persist tokens for subsequent requests (prevents stale CSRF token usage)
    if (response.data.access_token) {
      localStorage.setItem('portal_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('portal_refresh_token', response.data.refresh_token);
    }
    if (response.data.csrf_token) {
      localStorage.setItem('portal_csrf_token', response.data.csrf_token);
    }

    return response.data;
  }

  // Google SSO login
  async loginWithGoogle(idToken: string): Promise<LoginResponse> {
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/google/verify'),
      { id_token: idToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Extract tokens from nested AuthResponse structure
    const tokens = response.data.tokens;
    this.token = tokens.access_token;
    this.csrfToken = tokens.csrf_token || null;

    return {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      csrf_token: tokens.csrf_token,
      token_type: 'bearer',
      user_type: 'doctor',
      expires_in: tokens.expires_in,
      doctor: response.data.doctor,
    };
  }

  // Doctor registration (Step 1 - Account Creation). Same API as Android/iOS.
  async registerDoctor(email: string, password: string): Promise<{ message: string; email: string }> {
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/email/register'),
      { email, password },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return {
      message: response.data?.message ?? 'Registration successful. Please check your email to verify your account.',
      email: response.data?.email ?? email,
    };
  }

  // Resend doctor verification email (same as mobile).
  async resendDoctorVerificationEmail(email: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/doctor-auth/email/resend-verification'),
      { email },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data?.message ?? 'If an account exists, a verification email has been sent.';
  }

  // Request password reset
  async requestPasswordReset(email: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/auth/forgot-password'),
      { email, realm: 'doctor' },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return (
      response.data?.message ||
      'If this email exists, a password reset link has been sent.'
    );
  }

  // Verify password reset token
  async verifyResetToken(token: string): Promise<{ valid: boolean; message: string }> {
    const response = await axios.get(
      apiUrl(`/api/v1/auth/verify-reset-token/${encodeURIComponent(token)}`)
    );
    return {
      valid: Boolean(response.data?.valid),
      message: response.data?.message ?? '',
    };
  }

  // Complete password reset
  async resetPassword(token: string, newPassword: string): Promise<string> {
    const response = await axios.post(
      apiUrl('/api/v1/auth/reset-password'),
      { token, new_password: newPassword },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data?.message ?? 'Your password has been reset successfully.';
  }

  // Get stored token
  getToken(): string | null {
    return this.token || localStorage.getItem('portal_token');
  }

  // Get stored CSRF token
  getCsrfToken(): string | null {
    const memoryToken = this.csrfToken;
    const storageToken = localStorage.getItem('portal_csrf_token');
    return memoryToken || storageToken;
  }

  // Store tokens
  storeTokens(accessToken: string, refreshToken: string, csrfToken?: string): void {
    this.token = accessToken;
    this.csrfToken = csrfToken || null;
    localStorage.setItem('portal_token', accessToken);
    localStorage.setItem('portal_refresh_token', refreshToken);
    if (csrfToken) {
      localStorage.setItem('portal_csrf_token', csrfToken);
    }
  }

  // Clear tokens
  clearTokens(): void {
    // Best-effort server-side logout: revoke refresh token jti so it can’t mint new access tokens.
    // Fire-and-forget to keep this method synchronous.
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      axios
        .post(
          apiUrl('/api/v1/auth/logout'),
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .catch(() => {
          // ignore
        });
    }

    if (process.env.NODE_ENV !== 'production') {
      // Useful for tracking unexpected logouts in dev
      // eslint-disable-next-line no-console
      console.warn('[auth] clearTokens called', new Error().stack);
    }
    this.token = null;
    this.csrfToken = null;
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_refresh_token');
    localStorage.removeItem('portal_csrf_token');
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('portal_refresh_token');
  }

  // Refresh access token using refresh_token
  async refreshAccessToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      throw new Error('Missing refresh token');
    }

    const response = await axios.post(
      apiUrl('/api/v1/auth/refresh'),
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const newAccessToken = response.data.access_token as string;
    const newCsrfToken = response.data.csrf_token as string | undefined;

    // Update access token and CSRF token
    this.token = newAccessToken;
    localStorage.setItem('portal_token', newAccessToken);
    
    // Update CSRF token if provided
    if (newCsrfToken) {
      this.csrfToken = newCsrfToken;
      localStorage.setItem('portal_csrf_token', newCsrfToken);
    }

    return newAccessToken;
  }

  // Get or refresh CSRF token for authenticated users
  async ensureCsrfToken(): Promise<string | null> {
    // If we already have a CSRF token, return it
    const existingToken = this.getCsrfToken();
    if (existingToken) {
      return existingToken;
    }

    // If user is authenticated but CSRF token is missing, refresh the access token
    // which will also return a new CSRF token
    if (this.isAuthenticated()) {
      try {
        await this.refreshAccessToken();
        return this.getCsrfToken();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh CSRF token:', error);
        return null;
      }
    }

    return null;
  }

  // Get auth headers for API calls
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return headers;
  }
}

export const authService = new AuthService();

